import React, { useEffect, useState } from 'react';
import {
    Box,
    Center,
    ChevronLeftIcon,
    FlatList,
    HStack,
    VStack
} from 'native-base';
import {
    Product
} from '@springbig/shared/src/services/dutchie/schema-types';
import {
    useDutchieStore,
    useMenuQuery,
    useTheme,
} from '@springbig/shared';
import { H2 } from '~/shared/Typography';
import { useAppNavigate } from '..';
import { BrandsCard } from './components/BrandsCard';
import { ProductCard } from './components/ProductCard';
import { ShopHeader } from './components/ShopHeader';
import { useWindowSize } from 'usehooks-ts';
import { MenuResponse } from '@springbig/shared';
import { Section, Button } from '~/shared/Theme';
import { ShopStoreBottomSheet } from '~/pages/shop/components/StoreBottomSheet';

export const ProductListPage = () => {

    const {
        button_text,
    } = useTheme();

    const {
        retailer,
        menuType,
        productListProps,
        filters,
        sortByProp,
        showStoreBottomSheet,
        setShowStoreBottomSheet,
    } = useDutchieStore();

    const { mutate } = useMenuQuery();
    const { navigate, params } = useAppNavigate();

    const [offset, setOffset] =
        useState(0);

    const [products, setProducts] =
        useState<Array<Product> | undefined>();

    const [isDrawerOpen, setIsDrawerOpen] =
        useState(false);

    const effect = filters?.effects;
    const strain = filters?.strainType;
    const menuSort = sortByProp?.menuSort;
    const limit = 10;
    const {
        category,
        subCategory,
        menuSection,
        title,
        brand,
        search,
    } = productListProps ?? {};

    const { width } = useWindowSize();

    const hasFiltersEnabled =
        !!filters?.strainType || filters?.effects?.length > 0 || (filters?.minPrice || filters?.maxPrice);

    const createVariables = () => ({
        retailerId: retailer?.id,
        menuType,
        filter: {
            search,
            category,
            subCategory,
            effects: effect,
            strainType: strain,
            menuSection: menuSection ? { type: menuSection.type, specialId: menuSection.specialId } : undefined,
            brandId: brand?.id,
        },
        sort: { direction: menuSort?.direction, key: menuSort?.key },
        pagination: { offset, limit }
    });

    const filterProductsByPrice = (oldProducts: Product[], newProducts: Product[]) => {


        if (filters?.minPrice !== undefined && filters?.maxPrice !== undefined) {
            const minValue = filters.minPrice;
            const maxValue = filters.maxPrice;
            return [...oldProducts, ...newProducts].filter(e => {

                const price = menuType === 'MEDICAL' ? e.variants[0].specialPriceMed ?? e.variants[0].priceMed ?? 0 : e.variants[0].specialPriceRec ?? e.variants[0].priceRec ?? 0;
                return price <= maxValue && price >= minValue;
            });
        }
    };

    useEffect(() => {

        if (retailer && menuType && menuSort) {
            const variables = createVariables();
            mutate(variables, { onSuccess: (data: MenuResponse) => {


                if (data && data.menu.products.length) {
                    if (filters?.minPrice || filters?.maxPrice) {
                        setProducts(prevState => filterProductsByPrice(prevState ?? [], data.menu.products));
                    }
                    else {
                        setProducts(prevState => (offset === 0 ? data.menu.products : [...(prevState ?? []), ...data.menu.products]));
                    }
                }
            } });
        }
    }, [
        productListProps,
        retailer,
        menuType,
        category,
        subCategory,
        effect,
        strain,
        menuSection,
        limit,
        menuSort,
        offset,
        search,
        filters?.minPrice,
        filters?.maxPrice,
    ]);

    useEffect(() => {

        setOffset(0);
        setProducts([]);
    }, [
        category,
        subCategory,
        effect,
        strain,
        menuSection,
        limit,
        menuSort,
        search,
        filters?.minPrice,
        filters?.maxPrice,
    ]);

    return (
        <Section>
            <ShopHeader
                isDrawerOpen={isDrawerOpen}
                setIsDrawerOpen={(value) => setIsDrawerOpen(value)}
            />
            <VStack ml={2}>
                <HStack alignItems={'center'} justifyContent={'center'} space={4} p={2}>
                    <Button
                        onPress={() => navigate(`w/${params.wallet_id}/shop/`)}
                        position={'absolute'}
                        left={2}
                        justifyContent={'center'}
                        alignItems={'center'}
                        px={2}
                    >
                        <ChevronLeftIcon size={'lg'} color={button_text} />
                    </Button>
                    { brand ?
                        <Center w={'100%'} overflow={'hidden'}>
                            <BrandsCard brand={brand} />
                        </Center>
                        :
                        <H2>{ title }</H2>
                    }
                </HStack>
                { products?.length ?
                    <FlatList
                        _contentContainerStyle={ {  margin: 'auto' } }
                        horizontal={false}
                        numColumns={width < 425 ? 2 : 3}
                        data={products}
                        keyExtractor={(item : Product, index : number) => (`${item.id}-${index}-${item.name}`)}
                        renderItem={({ item, index }: { item: Product, index : number }) =>

                            <Box py={2}>
                                <ProductCard item={item} key={`${item.id}-${index}-${item.name}`} />
                            </Box>
                        }
                        onEndReached={({ distanceFromEnd }) => {

                            if (distanceFromEnd < 0) {
                                return;
                            }

                            setOffset(offset + limit);
                        }}
                        contentContainerStyle={{ flex: 1, margin: 'auto' }}
                        onEndReachedThreshold={0.8}
                    />
                    :
                    <></>
                }
            </VStack>
            <ShopStoreBottomSheet
                isOpen={showStoreBottomSheet}
                onClose={() => setShowStoreBottomSheet(!showStoreBottomSheet)}
            />
        </Section>
    );
};
